import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { _isNil, _isNotEmpty, _notNil } from '@/littledash';

import type { Cage, CageApiId } from '@/model/Cage.model';
import type { ID } from '@/model/Common.model';
import type { MetadataField, MetadataFieldValueTypes } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';

import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { createMeta } from '@/support/hot';
import { web as webRoute } from '@/support/route';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { clearSessionStorage, successToast } from '@/helpers';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import NoDataCard from '@/components/NoDataCard';
import Button from '@/components/UI/Button';
import Header from '@/components/UI/Header';
import Link from '@/components/UI/Link';
import SubHeader from '@/components/UI/SubHeader';

import { getMetaColumns, getMetaData } from '../Colony.utils';
import { initBulkCageSettings, initialBulkCageData } from '../Colony.utils';
import BulkCagesTable from './BulkCagesTable';
import '../Colony.scss';

interface BulkCageRow {
  id: ID;
  catalog: string;
  name: string;
  population: number;
  meta: Record<string, MetadataFieldValueTypes>;
}

interface CageUpdatePayload {
  api_id: CageApiId;
  name: string;
  meta: Record<string, MetadataFieldValueTypes>;
}

const BulkCageEdit: FC = () => {
  const navigate = useNavigate();

  const tableRef = useRef<HotTableRef>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<Array<MetadataField> | false>(false);
  const [metaColumns, setMetaColumns] = useState<Array<MetadataField> | false>(false);
  const [data, setData] = useState<Array<BulkCageRow>>([]);
  const [settings, setSettings] = useState<SpreadsheetSettings | null>(null);
  const [apiError, setApiError] = useState<Error | false>(false);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  const selectedCages: Array<Cage> = useMemo(
    () => JSON.parse(sessionStorage.getItem('selectedColonyCages') ?? '[]'),
    []
  );

  const { invoke: updateCages } = useApiHook({
    endpoint: 'PATCH /api/v1/cages',
    options: {
      invokeOnInit: false,
      onError: { throw: true, capture: true, toast: false, slug: 'bulk-cages-update' },
    },
  });

  useEffect(() => {
    return clearSessionStorage('selectedColonyCages', '/colony/edit/cages');
  }, []);

  const fetchMetadata = async (): Promise<void> => {
    setLoading(true);
    try {
      const metadataResponse = await getMetaData(MetadataTypes.collection_meta);
      const responseData = (metadataResponse?.body ?? []) as Array<MetadataField>;
      setMetadata(responseData);
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetadata();
    setData(initialBulkCageData(selectedCages) as Array<BulkCageRow>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (metaColumns === false && _isNotEmpty(data)) {
      if (metadata !== false && _isNotEmpty(metadata)) {
        setMetaColumns(getMetaColumns(selectedCages, metadata));
      } else {
        setMetaColumns([]);
      }
    }
  }, [metadata]);

  useEffect(() => {
    if (Array.isArray(metaColumns) && _isNil(settings)) {
      const metadataArray = metadata !== false ? metadata : [];

      const updateSettings = initBulkCageSettings(metaColumns, metadataArray);
      if (_notNil(updateSettings.settings)) {
        setSettings(updateSettings.settings);
      }
      if (_notNil(updateSettings.metadata)) {
        setMetadata(updateSettings.metadata);
      }
    }
  }, [metaColumns]);

  const putCages = async (cages: Array<CageUpdatePayload>): Promise<void> => {
    if (_isNotEmpty(cages)) {
      await updateCages({ body: { cages } });
    }
  };

  const bulkCagesSave = async (): Promise<void> => {
    if (isSaving || _isNil(settings)) {
      return;
    }

    setIsSaving(true);

    // The v1 endpoint identifies each cage by its api_id, whereas the spreadsheet keys rows by the
    // numeric cage id, so build a lookup to translate the cage id to its api_id. Metadata, however,
    // is sent as a flat map keyed by the numeric glossary id, matching the animals bulk-update
    // contract.
    const cageApiIdById = new Map<ID, CageApiId>(selectedCages.map((cage) => [cage.id, cage.api_id]));

    const cages: Array<CageUpdatePayload> = data.flatMap(({ id, name, meta }) => {
      const apiId = cageApiIdById.get(id);
      if (_isNil(apiId)) {
        return [];
      }

      let metaPayload: Record<string, MetadataFieldValueTypes> = {};
      if (_isNotEmpty(meta)) {
        const metaArray = createMeta(meta, settings.columns ?? []);
        metaPayload = metaArray.reduce<Record<string, MetadataFieldValueTypes>>((acc, item) => {
          acc[String(item.glossary_id)] = item.value;
          return acc;
        }, {});
      }

      return [{ api_id: apiId, name, meta: metaPayload }];
    });
    try {
      await putCages(cages);
      navigate('/colony');
      successToast(`Successfully edited ${cages.length > 1 ? 'cages' : 'cage'}`);
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetMetadata = (newMetadata: Array<MetadataField>): void => {
    setMetadata(newMetadata);
  };

  const spreadSheet = _notNil(settings) && (
    <>
      <div className="ow-spreadsheet-styles">
        <BulkCagesTable
          data={data as unknown as Array<Record<string, unknown>>}
          settings={settings}
          innerRef={tableRef as React.RefObject<HotTableRef>}
          metadata={metadata !== false ? metadata : []}
          setMetadata={handleSetMetadata}
          setInvalid={setIsInvalid}
          maxRows={data.length}
        />
      </div>
      <div className="pl4 pt4">
        <Button
          testId="bulk-cages__save"
          onClick={bulkCagesSave}
          disabled={loading || isInvalid || isSaving}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
        <Button plain className="ml3" onClick={() => navigate('/colony')}>
          Cancel
        </Button>
      </div>
    </>
  );

  return (
    <div className="h-100">
      {apiError !== false && (
        <ApiErrorBanner
          className="mb4"
          title={'There was an error with your request'}
          text={
            'An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support.'
          }
          error={apiError}
        />
      )}
      {loading ? (
        <Loading />
      ) : (
        <>
          <SubHeader linkToText="Colony" link={webRoute('colony')} />
          <div className="ph4">
            <Header mainHeaderText="Edit cages" />
          </div>
          {_isNotEmpty(selectedCages) ? (
            spreadSheet
          ) : (
            <div className="ma4">
              <NoDataCard
                title="You have not selected any cages"
                text={'Please select cages from inside Colony to proceed'}
                NoDataComponent={
                  <Link to={webRoute('colony')} className="link blue">
                    Go to Colony
                  </Link>
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BulkCageEdit;
